import { htmlEscape } from '@edicom/common/string/htmlEscape';
import { onDelegation } from '@edicom/common/event/onDelegation';
import { readMetadata } from './src/readMetadata';
import { generateTemplates } from './src/right/generateTemplates';
import { actionsFactory } from './src/left/actionsFactory';
import { createTreeDataFactory } from './src/left/createTreeDataFactory';
import { renderFormFactory } from './src/right/renderFormFactory';
import { getSelectTextFactory } from './src/getSelectTextFactory';
import { compileFactory } from './src/right/compileFactory';
import { getPathFromModelFactory } from './src/getPathFromModelFactory';
import { templateFn } from './src/templateFn';

fbTreeDirective.$inject = ['$compile', '$timeout', 'ecFormBuilderService'];

export default function fbTreeDirective($compile, $timeout, ecFormBuilderService) {
  return {
    restrict: 'E',
    scope: {
      fbFlash: '=',
    },
    require: '^^?ecFormBuilder',
    link,
  };

  function link(scope, element, attrs, ecFormBuilderController) {
    ecFormBuilderController = ecFormBuilderController || scope.ecFormBuilderController;
    const elem = element[0];

    // @odeniel Revisar si podemos usar el formBuilder y no el scope.$eval, problema:
    // const getValueFromScope = ecFormBuilderController.fn.getValueFromScope; // no admite lo "[" ej: xxx[0]
    // y ojo con CAB.EMB[0]['LIN'][0] que debería leer como [CAB,EMB,0,LIN,0]
    const getValueFromScope = exp => scope.$eval(exp);

    // binding del scope
    const parentRegisterPath = attrs.xPath
      .split('.')
      .splice(0, attrs.xPath.split('.').length - 1)
      .join('.');
    const parentRegisterName = parentRegisterPath.split('.').pop();

    ecFormBuilderController.bind(scope, parentRegisterPath);

    handleToggleEmpty();

    ecFormBuilderService.translate().then(locale => {
      const viewProperty = ecFormBuilderController.dispatcher.get(attrs.viewPropertyKey);
      const engineApi = ecFormBuilderController.dispatcher.get(attrs.engineApiKey);
      // cargamos el template manualmente
      templateFn(scope, elem, locale, $compile, viewProperty.metadata.tree);
      const eButtons = {
        inserts: [...elem.querySelectorAll('.js-fb-tree--btn--insert')],
        duplicate: elem.querySelector('.js-fb-tree--btn--duplicate'),
        remove: elem.querySelector('.js-fb-tree--btn--remove'),
      };

      const eTree = elem.querySelector('ewc-tree');

      const { config, treeNodes } = readMetadata(viewProperty, parentRegisterPath);
      const getPathFromModel = getPathFromModelFactory(parentRegisterPath);

      // right
      const templates = generateTemplates(viewProperty, engineApi, treeNodes);
      const compile = compileFactory(elem.querySelector('.fb-tree--form'), scope, ecFormBuilderController, $compile, $timeout);
      const renderForm = renderFormFactory(compile, templates, getValueFromScope, locale, getPathFromModel);

      // left
      const eToolbar = elem.querySelector('.fb-tree--toolbar-tree');
      const getSelectText = getSelectTextFactory(viewProperty.properties);
      const createTreeData = createTreeDataFactory(config, treeNodes, getValueFromScope, getPathFromModel, getSelectText);
      const findParentNode = node => eTree.api.getParent(node.id);
      const { create, insert, duplicate, remove } = actionsFactory(treeNodes, getValueFromScope, getPathFromModel, createTreeData, findParentNode);
      const onClick = onDelegation('action', {
        create: () =>
          create().then(() => {
            scope.fbTreeForm.$setDirty();
            refreshTree();
          }),
        insert: event => {
          const path = event.target.getAttribute('child');
          if (path) {
            insert(eTree.selected, path).then(res => {
              scope.fbTreeForm.$setDirty();
              refreshTree(res);
            });
          }
        },
        duplicate: () =>
          duplicate(eTree.selected).then(nodes => {
            scope.fbTreeForm.$setDirty();
            refreshTree(nodes);
          }),
        remove: () =>
          remove(eTree.selected).then(() => {
            eTree.selectedIds = [];
            scope.fbTreeForm.$setDirty();
            refreshTree();
          }),
      });

      // render initial
      renderTree();

      // register events
      eTree.addEventListener('EwcTree:select', onTreeSelect);
      elem.addEventListener('click', onClick);

      elem.addEventListener('input', onInput);

      scope.$on('$destroy', () => {
        eTree.removeEventListener('EwcTree:select', onTreeSelect);
        elem.removeEventListener('click', onClick);
      });

      //////////////////////////////////////////////////////////////////////////

      function renderTree(res) {
        if (!res) {
          // si no recibimos ningún nodo, reconstruimos el tree entero
          return createTreeData(scope[parentRegisterName], parentRegisterPath).then(data => {
            eTree.render(data, { template });
          });
        }

        // si recibimos nodos, es porque solo queremos refrescarlos esos.
        if (res.length === 1) {
          const node = res[0].node;
          return eTree.refreshNode(node.id, node).then(() => {
            if (node.children && node.children.length) {
              eTree.show(node.children[0].id);
              const nodeToFlash = eTree.shadowRoot.querySelector(`li[data-id="${res[0].newId}"]`);
              eTree.flashTo(nodeToFlash);
            }
          });
        }
        /**
         * Devuelve una promesa o string que devuelve el html (en string) de un nodo del tree
         * @param {object} data los datos del nodo `node.data`
         * @return {string}
         */
        function template(data) {
          const title = data.getTitle();
          if (title) {
            return `<span>${title}</span>`;
          }
          return `<span ec-text="muted">${htmlEscape(`<${locale['fbTree.empty']}>`)}</span>`;
        }
      }

      function refreshTree(res) {
        return new Promise(resolve => {
          handleToggleEmpty();
          scope.$evalAsync();
          // $timeout(() => {
          renderTree(res).then(() => {
            eTree.selectedIds = eTree.selected.map(({ id }) => id);
            resolve();
          });
          // });
        });
      }

      // events

      function onTreeSelect() {
        if (eTree.selected.length) {
          eButtons.duplicate.removeAttribute('disabled');
          eButtons.remove.removeAttribute('disabled');
          if (eTree.selected.length === 1) {
            showButtonInsert(eTree.selected[0]);
            renderForm(eTree.selected[0].id); // el id es el model
          } else {
            showButtonInsert();
            renderForm(); // si no hay 1 nodo y solo uno, mostramos un mensaje a la derecha
          }
        } else {
          showButtonInsert();
          eButtons.duplicate.setAttribute('disabled', '');
          eButtons.remove.setAttribute('disabled', '');
          renderForm(); // si no hay 1 nodo y solo uno, mostramos un mensaje a la derecha
        }
      }

      function showButtonInsert(node) {
        // primero escondemos todos los botones
        for (let i = 0, len = eButtons.inserts.length; i < len; i++) {
          eButtons.inserts[i].setAttribute('hidden', '');
        }
        // ahora buscamos el que tenemos que mostrar:

        if (!node) {
          // entonces es que hay 0 o mas de 1 seleccionado, mostramos el disabled
          eButtons.inserts[0].removeAttribute('hidden');
        } else {
          // aqui es que tenemos 1 nodo seleccionado, entonces buscamos su boton para mostrarlo
          // usamos endsWith porque la rutas en el node pueden ser relativas
          const eBtnCurrent = eButtons.inserts.find(eBtn => node.path.endsWith(eBtn.getAttribute('node') || 'no queremos este button'));
          if (eBtnCurrent) {
            eBtnCurrent.removeAttribute('hidden');
          }
        }

        // refrescamos el toolbar para el responsive
        eToolbar.refresh();
      }

      function onInput() {
        const selected = eTree.selected;
        if (selected.length !== 1 || !selected[0].getTitle.isDinamic) {
          // si no hay nodo seleccionado
          // o si no es dinamico, no hacemos nada
          return;
        }
        // refrescamos el nodo para actualizar su titulo
        eTree.refreshNode(selected[0].id);
      }
    });

    /**
     * Se encarga del toggle del atributo `empty` para que en css se muestra lo que toca
     */
    function handleToggleEmpty() {
      const isEmpty = (getValueFromScope(attrs.data) || []).length === 0;
      if (isEmpty) {
        !elem.hasAttribute('empty') && elem.setAttribute('empty', '');
      } else {
        elem.hasAttribute('empty') && elem.removeAttribute('empty');
      }
    }
  }
}
